/**
 * Lag smoothing's rules, apart from the page (so they can be tested on their own); `lagSmoothing.ts` puts them to work.
 *
 * When a player's ping stays high, the server's updates also arrive unevenly: a few at once, then none for a moment. The
 * game normally eases each object from its last position to its new one over the time since the update before, so an
 * uneven stream shows as jumps and stops. Lag smoothing eases from where each object is drawn now, over a little more
 * than the usual time between updates, so it keeps moving evenly between them.
 */

/** above this ping (ms) for long enough, lag smoothing switches on; back at or under it for as long, off again */
export const LAG_SMOOTHING_PING_MS = 180;

/** how long the ping must stay above the line (or back under it) before smoothing switches: more than 10 seconds */
export const LAG_SMOOTHING_HOLD_MS = 10_000;

/** the ping is read as the middle value of the last 2 seconds of measurements, so one odd measurement doesn't count */
export const PING_WINDOW_MS = 2_000;

const median = (values: readonly number[]): number => {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = sorted.length >> 1;
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

/** whether lag smoothing should be on, from the ping measurements (one per answered input, many a second) */
export class PingWatch {
    private readonly _samples: Array<{ readonly at: number, readonly ms: number }> = [];
    private _aboveSince?: number;
    private _belowSince?: number;

    private _on = false;
    get on(): boolean { return this._on; }

    private _level?: number;
    /** the ping as read now (the middle of the last 2 seconds), once there's a measurement */
    get level(): number | undefined { return this._level; }

    /** adds one measurement taken at `at` (ms); true when smoothing switched on or off with it */
    add(ms: number, at: number): boolean {
        if (!Number.isFinite(ms) || ms < 0 || !Number.isFinite(at)) return false;
        this._samples.push({ at, ms });
        while (this._samples.length > 1 && at - this._samples[0].at > PING_WINDOW_MS) this._samples.shift();
        const level = this._level = median(this._samples.map(sample => sample.ms));

        if (level > LAG_SMOOTHING_PING_MS) {
            this._belowSince = undefined;
            this._aboveSince ??= at;
            if (!this._on && at - this._aboveSince > LAG_SMOOTHING_HOLD_MS) {
                this._on = true;
                return true;
            }
        } else {
            this._aboveSince = undefined;
            this._belowSince ??= at;
            if (this._on && at - this._belowSince > LAG_SMOOTHING_HOLD_MS) {
                this._on = false;
                return true;
            }
        }
        return false;
    }

    reset(): void {
        this._samples.length = 0;
        this._aboveSince = this._belowSince = this._level = undefined;
        this._on = false;
    }
}

/** how the server's updates arrive: their usual spacing and how uneven it is (both smoothed over the last few dozen) */
export class TickWatch {
    private _last?: number;
    private _mean: number;
    private _jitter = 0;

    /** @param nominal the time between two server ticks (ms) */
    constructor(private readonly _nominal: number) {
        this._mean = _nominal;
    }

    /** an update arrived at `at` (ms) */
    add(at: number): void {
        const last = this._last;
        this._last = at;
        if (last === undefined) return;
        const gap = at - last;
        // a pause (a hidden tab, a reconnect) says nothing about the connection's rhythm
        if (!(gap >= 0) || gap > 1_000) return;
        this._mean += (gap - this._mean) * 0.1;
        this._jitter += (Math.abs(gap - this._mean) - this._jitter) * 0.1;
    }

    /**
     * How long each new position is eased in over: a little more than the usual gap between updates, so the next one is
     * nearly always there before an object stops; never under one tick, never over three (so it never trails far behind).
     */
    get window(): number {
        return Math.min(Math.max(this._mean + 2 * this._jitter, this._nominal), this._nominal * 3);
    }

    reset(): void {
        this._last = undefined;
        this._mean = this._nominal;
        this._jitter = 0;
    }
}

/** a jump this long (game units) is a teleport (a respawn, a new player to watch): drawn at once, never slid across */
export const LAG_SMOOTHING_TELEPORT_DISTANCE = 20;
